var KLAVIYO_SUBSCRIBE = "https://a.klaviyo.com/client/subscriptions/";
var KLAVIYO_EVENTS = "https://a.klaviyo.com/client/events/";
var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isSameOriginUrl(url) {
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch (err) {
    return false;
  }
}

class ShopUsedToggle extends HTMLElement {
  connectedCallback() {
    this._tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this._panes = {
      new: this.querySelector(".zeus-shop-used__pane--new"),
      used: this.querySelector(".zeus-shop-used__pane--used"),
    };
    this._productInfo = this.closest(".product-info");
    this._resultsUrl = this.dataset.resultsUrl;
    this._clusterTypes = (this.dataset.buyClusterTypes || "")
      .split(",")
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
    this._loaded = false;
    this._showWhenEmpty = this.dataset.showWhenEmpty === "true";
    this._fetchAbort = new AbortController();
    this._tabAbort = new AbortController();
    this._copy = this._readCopySettings();

    var self = this;
    this._tabs.forEach(function (tab, i) {
      tab.addEventListener(
        "click",
        function () {
          self._activate(i);
        },
        { signal: self._tabAbort.signal },
      );
      tab.addEventListener(
        "keydown",
        function (e) {
          self._onKeydown(e, i);
        },
        { signal: self._tabAbort.signal },
      );
    });

    this._onWaitlistSubmit = this._handleWaitlistSubmit.bind(this);
    this.addEventListener("submit", this._onWaitlistSubmit);

    if (!this._showWhenEmpty) {
      this._verifyStockOnLoad();
    }
  }

  _readCopySettings() {
    var d = this.dataset;
    // Fallbacks are English-only last resort — Liquid always sets data-copy-* from locales.
    return {
      cardCta: d.copyCardCta || "View",
      badgeReconditioned: d.copyBadgeReconditioned || "Refurbished",
      badgeUsed: d.copyBadgeUsed || "Pre-owned",
      badgeGradePrefix: d.copyBadgeGradePrefix || "Grade",
      waitlistEyebrow: d.copyWaitlistEyebrow || "Stock alert",
      waitlistText:
        d.copyWaitlistText ||
        "No pre-owned board available for this model right now. Leave your email — we'll ping you when one lands.",
      waitlistEmailLabel: d.copyWaitlistEmailLabel || "Your email",
      waitlistPlaceholder: d.copyWaitlistPlaceholder || "you@email.com",
      waitlistSubmit: d.copyWaitlistSubmit || "Notify me",
      waitlistConsent: d.copyWaitlistConsent || "Double opt-in.",
      waitlistFormLabel: d.copyWaitlistFormLabel || "Pre-owned stock alert",
      waitlistEmailInvalid: d.copyWaitlistEmailInvalid || "Enter a valid email address.",
      waitlistSuccess: d.copyWaitlistSuccess || "Got it. Check your inbox to confirm.",
      waitlistError: d.copyWaitlistError || "Could not subscribe right now. Try again in a moment.",
      waitlistUnavailable: d.copyWaitlistUnavailable || "Signup unavailable right now.",
      loaderText: d.loaderText || "Loading…",
      loaderError: d.loaderError || "Could not load pre-owned boards.",
    };
  }

  _applyCopySettings(root) {
    if (!root || !this._copy) return;

    var c = this._copy;
    var eyebrow = root.querySelector(".zeus-shop-used-waitlist__eyebrow");
    var text = root.querySelector(".zeus-shop-used-waitlist__text");
    var form = root.querySelector(".zeus-shop-used-waitlist__form");
    var emailLabel = root.querySelector(".zeus-shop-used-waitlist__row label");
    var emailInput = root.querySelector(".zeus-shop-used-waitlist__input");
    var submitBtn = root.querySelector(".zeus-shop-used-waitlist__form [type='submit']");
    var consent = root.querySelector(".zeus-shop-used-waitlist__consent");

    if (eyebrow) eyebrow.textContent = c.waitlistEyebrow;
    if (text) text.textContent = c.waitlistText;
    if (form) form.setAttribute("aria-label", c.waitlistFormLabel);
    if (emailLabel) emailLabel.textContent = c.waitlistEmailLabel;
    if (emailInput) emailInput.setAttribute("placeholder", c.waitlistPlaceholder);
    if (submitBtn) submitBtn.textContent = c.waitlistSubmit;
    if (consent) {
      consent.textContent = c.waitlistConsent;
      consent.hidden = !c.waitlistConsent;
    }

    root.querySelectorAll(".zeus-shop-used-card__cta").forEach(function (el) {
      el.textContent = c.cardCta;
    });

    root.querySelectorAll(".zeus-shop-used-card__badges .zeus-pill--sky").forEach(function (el) {
      el.textContent = c.badgeReconditioned;
    });
    root.querySelectorAll(".zeus-shop-used-card__badges .zeus-pill--accent").forEach(function (el) {
      el.textContent = c.badgeUsed;
    });
    root.querySelectorAll(".zeus-shop-used-card__badges .zeus-pill--navy[data-grade]").forEach(function (el) {
      el.textContent = c.badgeGradePrefix + " " + el.dataset.grade;
    });
  }

  disconnectedCallback() {
    if (this._fetchAbort) this._fetchAbort.abort();
    if (this._tabAbort) this._tabAbort.abort();
    if (this._onWaitlistSubmit) {
      this.removeEventListener("submit", this._onWaitlistSubmit);
    }
  }

  _activate(index) {
    var isUsed = index === 1;

    this._tabs.forEach(function (tab, i) {
      tab.setAttribute("aria-selected", String(i === index));
    });

    if (isUsed) {
      if (this._panes.new) this._panes.new.setAttribute("hidden", "");
      if (this._panes.used) this._panes.used.removeAttribute("hidden");
      if (this._productInfo) this._productInfo.classList.add("is-shop-used");
      this._hideCluster();
      if (!this._loaded) this._loadResults();
    } else {
      if (this._panes.new) this._panes.new.removeAttribute("hidden");
      if (this._panes.used) this._panes.used.setAttribute("hidden", "");
      if (this._productInfo) this._productInfo.classList.remove("is-shop-used");
      this._restoreCluster();
    }

    document.dispatchEvent(
      new CustomEvent("zeus:shop-used-tab", {
        bubbles: true,
        detail: { tab: isUsed ? "used" : "new" },
      }),
    );
  }

  _onKeydown(e, index) {
    var next = index;
    if (e.key === "ArrowRight") next = (index + 1) % this._tabs.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + this._tabs.length) % this._tabs.length;
    else return;

    e.preventDefault();
    this._tabs[next].focus();
    this._activate(next);
  }

  _hideCluster() {
    if (!this._productInfo || !this._clusterTypes.length) return;
    var self = this;
    this._clusterTypes.forEach(function (type) {
      var block = self._productInfo.querySelector('[data-block-type="' + type + '"]');
      if (block) {
        block.setAttribute("data-shop-used-hidden", "");
        block.setAttribute("aria-hidden", "true");
        block.style.display = "none";
        block.querySelectorAll("a, button, input, select, textarea, [tabindex]").forEach(function (el) {
          el.dataset.shopUsedTabindex = el.getAttribute("tabindex") || "";
          el.setAttribute("tabindex", "-1");
        });
      }
    });
  }

  _restoreCluster() {
    if (!this._productInfo) return;
    var blocks = this._productInfo.querySelectorAll("[data-shop-used-hidden]");
    blocks.forEach(function (block) {
      block.removeAttribute("data-shop-used-hidden");
      block.removeAttribute("aria-hidden");
      block.style.display = "";
      block.querySelectorAll("[data-shop-used-tabindex]").forEach(function (el) {
        var prev = el.dataset.shopUsedTabindex;
        if (prev === "") el.removeAttribute("tabindex");
        else el.setAttribute("tabindex", prev);
        delete el.dataset.shopUsedTabindex;
      });
    });
  }

  _hasStockInResults(doc) {
    var list = doc.querySelector(".zeus-shop-used-results__list");
    return !!(list && list.querySelector(".zeus-shop-used-results__item"));
  }

  _hideToggle() {
    this.hidden = true;
    if (this._productInfo) this._productInfo.classList.remove("is-shop-used");
    this._restoreCluster();
  }

  async _verifyStockOnLoad() {
    if (!this._resultsUrl || !isSameOriginUrl(this._resultsUrl)) return;

    try {
      var res = await fetch(this._resultsUrl, { signal: this._fetchAbort.signal });
      if (!res.ok) throw new Error(String(res.status));
      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, "text/html");
      if (!this._hasStockInResults(doc)) this._hideToggle();
    } catch (err) {
      if (err.name === "AbortError") return;
      /* Fail-open: keep toggle visible on network errors; only hide after a successful empty probe. */
    }
  }

  _setLoaderMessage(message) {
    var loader = this._panes.used ? this._panes.used.querySelector(".zeus-shop-used__loader") : null;
    if (loader) loader.textContent = message || (this._copy && this._copy.loaderText) || "Chargement…";
  }

  async _loadResults() {
    if (!this._resultsUrl || !isSameOriginUrl(this._resultsUrl)) {
      console.warn("[zeus-shop-used] blocked non-same-origin results fetch");
      this._setLoaderMessage(this._copy.loaderError);
      return;
    }

    try {
      var res = await fetch(this._resultsUrl, { signal: this._fetchAbort.signal });
      if (!res.ok) throw new Error(String(res.status));
      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, "text/html");
      var inner = doc.querySelector(".zeus-shop-used-results");
      if (inner && this._panes.used) {
        if (!this._showWhenEmpty && !this._hasStockInResults(doc)) {
          this._hideToggle();
          return;
        }
        this._panes.used.replaceChildren(inner);
        this._applyCopySettings(inner);
        this._loaded = true;
        return;
      }
      throw new Error("missing results");
    } catch (err) {
      if (err.name === "AbortError") return;
      this._setLoaderMessage(this._copy.loaderError);
    }
  }

  async _handleWaitlistSubmit(e) {
    var form = e.target.closest(".zeus-shop-used-waitlist__form");
    if (!form || !this.contains(form)) return;

    e.preventDefault();

    var emailInput = form.querySelector('[name="email"]');
    var status = form.querySelector(".zeus-shop-used-waitlist__status");
    var submitBtn = form.querySelector('[type="submit"]');
    var email = emailInput ? emailInput.value.trim() : "";
    var model = form.dataset.model || this.dataset.model || "";

    if (!EMAIL_RE.test(email)) {
      if (status) status.textContent = this._copy.waitlistEmailInvalid;
      if (emailInput) emailInput.focus();
      return;
    }

    var companyId = this.dataset.klaviyoCompanyId;
    var listId = this.dataset.klaviyoListId;
    var revision = this.dataset.klaviyoRevision || "2024-10-15";

    if (!companyId || !listId) {
      console.warn("[zeus-shop-used] missing Klaviyo company_id or list_id; subscribe skipped");
      if (status) status.textContent = this._copy.waitlistUnavailable;
      return;
    }

    if (submitBtn) submitBtn.disabled = true;
    if (status) status.textContent = "";

    var profileProps = { used_model_interests: model };

    try {
      var results = await Promise.allSettled([
        this._klaviyoPost(KLAVIYO_SUBSCRIBE, companyId, revision, {
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Used Alert Waitlist",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: email,
                    properties: profileProps,
                  },
                },
              },
            },
            relationships: { list: { data: { type: "list", id: listId } } },
          },
        }),
        this._klaviyoPost(KLAVIYO_EVENTS, companyId, revision, {
          data: {
            type: "event",
            attributes: {
              metric: { data: { type: "metric", attributes: { name: "Subscribed to Used Alert" } } },
              properties: { model_handle: model, email: email },
              profile: { data: { type: "profile", attributes: { email: email, properties: profileProps } } },
            },
          },
        }),
      ]);

      var subscribeResult = results[0];
      if (subscribeResult.status === "rejected") throw subscribeResult.reason;
      if (results[1].status === "rejected") {
        console.warn("[zeus-shop-used] Klaviyo event tracking failed", results[1].reason);
      }

      if (status) {
        status.textContent = this._copy.waitlistSuccess;
      }
      if (emailInput) emailInput.value = "";
    } catch (err) {
      console.warn("[zeus-shop-used] Klaviyo subscribe failed", err);
      if (status) status.textContent = this._copy.waitlistError;
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  _klaviyoPost(url, companyId, revision, payload) {
    return fetch(url + "?company_id=" + encodeURIComponent(companyId), {
      method: "POST",
      headers: { "Content-Type": "application/json", revision: revision },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error("Klaviyo " + res.status);
    });
  }
}

if (!customElements.get("shop-used-toggle")) {
  customElements.define("shop-used-toggle", ShopUsedToggle);
}

document.addEventListener("zeus:shop-used-tab", function (e) {
  var isUsed = e.detail.tab === "used";
  var atcBtn = document.querySelector(".zeus-sticky-nav__atc");
  if (!atcBtn) return;

  if (isUsed) {
    atcBtn.disabled = true;
  } else {
    var mainAtc = document.querySelector('[data-block-type="buy-buttons"] button[type="submit"]');
    atcBtn.disabled = mainAtc ? mainAtc.disabled : false;
  }
  atcBtn.setAttribute("aria-disabled", String(atcBtn.disabled));

  var stickyWrap = atcBtn.closest('[class*="zeus-sticky"]');
  if (stickyWrap) stickyWrap.classList.toggle("is-shop-used", isUsed);
});
